import { Injectable, computed, signal } from "@angular/core";
import type {
	GameSessionState,
	ServerMessage,
	ClientMessage,
} from "@magpie/types";
import { environment } from "./environment";

export type ConnectionState =
	| "disconnected"
	| "connecting"
	| "connected"
	| "reconnecting";

const HEARTBEAT_INTERVAL_MS = 30_000;
const STALE_CONNECTION_MS = 45_000;
const HEARTBEAT_CHECK_MS = 5_000;

@Injectable({
	providedIn: "root",
})
export class PartyService {
	readonly gameState = signal<GameSessionState | null>(null);
	readonly connectionState = signal<ConnectionState>("disconnected");
	readonly isConnected = computed(() => this.connectionState() === "connected");
	readonly isReconnecting = computed(
		() => this.connectionState() === "reconnecting",
	);

	private ws: WebSocket | null = null;
	private roomId: string | null = null;
	private reconnectAttempts = 0;
	private maxReconnectAttempts = 15;
	private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
	private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
	private staleCheckTimer: ReturnType<typeof setInterval> | null = null;
	private lastMessageAt = 0;

	connect(roomId: string) {
		this.roomId = roomId;
		this.reconnectAttempts = 0;
		this.connectionState.set("connecting");
		this.openConnection();
	}

	disconnect() {
		this.roomId = null;
		this.reconnectAttempts = this.maxReconnectAttempts; // prevent reconnect
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}
		this.stopHeartbeat();
		if (this.ws) {
			this.ws.close();
			this.ws = null;
		}
		this.connectionState.set("disconnected");
		this.gameState.set(null);
	}

	send(message: ClientMessage) {
		if (this.ws?.readyState === WebSocket.OPEN) {
			this.ws.send(JSON.stringify(message));
		}
	}

	private openConnection() {
		if (!this.roomId) return;

		const partyUrl = environment.partyUrl ?? "http://127.0.0.1:5511";
		const wsProtocol = partyUrl.startsWith("https") ? "wss" : "ws";
		const host = partyUrl.replace(/^https?:\/\//, "");
		const url = `${wsProtocol}://${host}/party/${this.roomId}`;

		this.ws = new WebSocket(url);

		this.ws.onopen = () => {
			this.connectionState.set("connected");
			this.reconnectAttempts = 0;
			this.lastMessageAt = Date.now();
			this.startHeartbeat();
		};

		this.ws.onmessage = (event) => {
			this.lastMessageAt = Date.now();
			try {
				const msg = JSON.parse(event.data) as ServerMessage;
				this.handleMessage(msg);
			} catch (err) {
				console.warn("PartyService: failed to handle message", err, event.data);
			}
		};

		this.ws.onclose = () => {
			this.stopHeartbeat();
			this.attemptReconnect();
		};

		this.ws.onerror = () => {
			// onclose will fire after onerror
		};
	}

	private handleMessage(msg: ServerMessage) {
		const state = this.gameState();

		switch (msg.type) {
			case "game_state":
				this.gameState.set(msg.state);
				break;

			case "player_joined":
				if (state) {
					const existing = state.participants.find(
						(p) => p.userId === msg.participant.userId,
					);
					const participants = existing
						? state.participants.map((p) =>
								p.userId === msg.participant.userId ? msg.participant : p,
							)
						: [...state.participants, msg.participant];
					this.gameState.set({ ...state, participants });
				}
				break;

			case "player_left":
				if (state) {
					const participants = state.participants.map((p) =>
						p.userId === msg.userId ? { ...p, isConnected: false } : p,
					);
					this.gameState.set({ ...state, participants });
				}
				break;

			case "game_started":
				if (state) {
					this.gameState.set({
						...state,
						status: "active" as any,
						startedAt: msg.startedAt,
						endsAt: msg.endsAt,
					});
				}
				break;

			case "submission_recorded":
				if (state) {
					const idx = state.submissions.findIndex(
						(s) =>
							s.userId === msg.submission.userId &&
							s.roundId === msg.submission.roundId &&
							s.itemId === msg.submission.itemId,
					);
					const submissions =
						idx >= 0
							? state.submissions.map((s, i) =>
									i === idx ? msg.submission : s,
								)
							: [...state.submissions, msg.submission];
					this.gameState.set({ ...state, submissions });
				}
				break;

			case "round_unlocked":
				if (state) {
					const rounds = state.rounds.map((r) =>
						r.roundId === msg.roundId
							? {
									...r,
									isUnlocked: true,
									unlockedAt: msg.unlockedAt,
									closesAt: msg.closesAt,
								}
							: r,
					);
					this.gameState.set({ ...state, rounds });
				}
				break;

			case "round_closed":
				if (state) {
					const rounds = state.rounds.map((r) =>
						r.roundId === msg.roundId ? { ...r, isClosed: true } : r,
					);
					this.gameState.set({ ...state, rounds });
				}
				break;

			case "game_ended":
				if (state) {
					this.gameState.set({
						...state,
						status: "completed" as any,
						remainingSeconds: 0,
					});
				}
				break;

			case "timer_tick":
				if (state) {
					this.gameState.set({
						...state,
						remainingSeconds: msg.remainingSeconds,
					});
				}
				break;

			case "pong":
				// heartbeat ack — lastMessageAt already updated
				break;
		}
	}

	private attemptReconnect() {
		if (!this.roomId || this.reconnectAttempts >= this.maxReconnectAttempts) {
			this.connectionState.set("disconnected");
			return;
		}

		this.connectionState.set("reconnecting");
		this.reconnectAttempts++;

		// Exponential backoff with jitter: base * 2^attempt + random(0..500)ms
		const exp = Math.min(1000 * 2 ** this.reconnectAttempts, 30_000);
		const jitter = Math.random() * 500;
		const delay = exp + jitter;
		this.reconnectTimer = setTimeout(() => this.openConnection(), delay);
	}

	private startHeartbeat() {
		this.stopHeartbeat();
		this.heartbeatTimer = setInterval(() => {
			this.send({ type: "ping" });
		}, HEARTBEAT_INTERVAL_MS);

		this.staleCheckTimer = setInterval(() => {
			if (Date.now() - this.lastMessageAt > STALE_CONNECTION_MS) {
				console.warn(
					"PartyService: connection appears stale, forcing reconnect",
				);
				this.stopHeartbeat();
				if (this.ws) {
					// Force close; onclose will trigger reconnect
					try {
						this.ws.close();
					} catch {
						/* ignore */
					}
				}
			}
		}, HEARTBEAT_CHECK_MS);
	}

	private stopHeartbeat() {
		if (this.heartbeatTimer) {
			clearInterval(this.heartbeatTimer);
			this.heartbeatTimer = null;
		}
		if (this.staleCheckTimer) {
			clearInterval(this.staleCheckTimer);
			this.staleCheckTimer = null;
		}
	}
}
